import React from 'react';

const number = value => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const rupees = value => `₹${number(value)}`;
const money = value => Math.round(Number(value || 0) * 100) / 100;

const vendorName = value => {
  const names = [...new Set((Array.isArray(value) ? value : [value])
    .map(name => String(name || '').trim())
    .filter(Boolean))];
  const assigned = names.filter(name => name.toLowerCase() !== 'unassigned');
  return (assigned.length ? assigned : names).join(', ') || 'Unassigned';
};

const statusDetails = value => {
  const status = String(value || '').toUpperCase();
  if (status === 'PROFITABLE') return ['Profitable', 'positive'];
  if (status === 'LOSS') return ['Loss', 'negative'];
  if (status === 'BREAK EVEN') return ['Break even', 'neutral'];
  return ['Billing not recorded', 'missing'];
};

const dateFromValue = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
};

const addMonths = (date, months) => {
  const day = date.getUTCDate();
  const next = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const lastDay = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate();
  next.setUTCDate(Math.min(day, lastDay));
  return next;
};

const emiDueInRange = (loan, range) => {
  const amount = Number(loan.emiAmount || 0);
  const firstDueDate = dateFromValue(loan.emiDueDate || loan.startDate);
  const start = dateFromValue(range?.startDate);
  const end = dateFromValue(range?.endDate);
  const installments = Number(loan.tenureMonths || 0);
  if (!(amount > 0) || !firstDueDate || !start || !end || !Number.isSafeInteger(installments) || installments < 1) return 0;

  let total = 0;
  for (let index = 0; index < installments; index += 1) {
    const dueDate = addMonths(firstDueDate, index);
    if (dueDate > end) break;
    if (dueDate >= start) total += amount;
  }
  return money(total);
};

export default function TopVehiclePerformance({ rows = [], contracts = [], vendorAgreements = [], loans = [], range, total }) {
  const contractByVehicle = new Map(contracts.map(contract => [String(contract.vehicle?._id || contract.vehicle), contract]));
  const vendorByVehicle = new Map(vendorAgreements.map(agreement => [String(agreement.vehicle?._id || agreement.vehicle), agreement]));
  const loanEmiByVehicle = new Map();

  loans.forEach(loan => {
    const vehicleId = String(loan.vehicle?._id || loan.vehicle || '');
    if (!vehicleId) return;
    loanEmiByVehicle.set(vehicleId, money((loanEmiByVehicle.get(vehicleId) || 0) + emiDueInRange(loan, range)));
  });

  return <section className="ba-performance" aria-labelledby="top-vehicle-performance-title">
    <div className="ba-performance-heading">
      <div>
        <h3 id="top-vehicle-performance-title">Top Vehicle Performance</h3>
        <p>Profit/loss includes fixed rental, vendor billing, fuel, operating expenses, and loan EMIs due in the selected period.</p>
      </div>
      <span>{total ?? rows.length} vehicles</span>
    </div>
    <div className="ba-performance-table">
      <table>
        <thead><tr>
          <th>Vehicle Number</th>
          <th>Vendor Name</th>
          <th>Contract Type</th>
          <th>Client Fixed Rental</th>
          <th>Vendor Fixed Billing</th>
          <th>Vehicle Present</th>
          <th>Fuel</th>
          <th>Expenses</th>
          <th>Loan EMI</th>
          <th>Profit / Loss</th>
          <th>Margin %</th>
          <th>Status</th>
        </tr></thead>
        <tbody>{rows.length ? rows.map(row => {
          const vehicleId = String(row.vehicleId || row._id);
          const contract = contractByVehicle.get(vehicleId);
          const activeContract = contract && (contract.status || 'active') === 'active';
          const isRental = contract?.contractType === 'rental';
          const hasFixedRental = activeContract && Number(contract.fixedRate) > 0 && (isRental || (Number.isSafeInteger(Number(contract.contractDays)) && Number(contract.contractDays) > 0));
          const fixedRental = hasFixedRental
            ? (isRental ? money(contract.fixedRate) : money(Number(contract.fixedRate) / Number(contract.contractDays) * Number(row.present || 0)))
            : 0;
          const hasRevenue = hasFixedRental || Number(row.billingCount || 0) > 0;
          const revenue = hasFixedRental ? fixedRental : money(row.revenue);

          const vendorAgreement = vendorByVehicle.get(vehicleId);
          const hasVendorBilling = vendorAgreement && (vendorAgreement.status || 'active') === 'active' && Number(vendorAgreement.fixedRate) > 0 && Number.isSafeInteger(Number(vendorAgreement.contractDays)) && Number(vendorAgreement.contractDays) > 0;
          const vendorBilling = hasVendorBilling ? money(Number(vendorAgreement.fixedRate) / Number(vendorAgreement.contractDays) * Number(row.present || 0)) : 0;
          const fuel = money(row.fuelCost);
          const nonFuelExpenses = Math.max(0, money(Number(row.expenses || 0) - fuel));
          const loanEmi = loanEmiByVehicle.get(vehicleId) || 0;
          const hasCosts = vendorBilling > 0 || fuel > 0 || nonFuelExpenses > 0 || loanEmi > 0;
          const profit = hasRevenue || hasCosts ? money(revenue - vendorBilling - fuel - nonFuelExpenses - loanEmi) : null;
          const margin = profit != null && revenue ? money(profit / revenue * 100) : null;
          const [status, tone] = statusDetails(hasRevenue ? (profit > 0 ? 'PROFITABLE' : profit < 0 ? 'LOSS' : 'BREAK EVEN') : 'BILLING NOT RECORDED');

          return <tr key={row._id || row.vehicleId || row.name}>
            <td><strong>{row.name || 'Unassigned'}</strong></td>
            <td>{vendorName(row.vendor)}</td>
            <td>{hasFixedRental ? <span className="ba-contract-type">{isRental ? 'Rental' : 'Monthly contract'}</span> : '—'}</td>
            <td>{hasRevenue ? <div className={hasFixedRental ? 'ba-contract-billing' : undefined}><strong>{rupees(revenue)}</strong>{hasFixedRental && <small>{isRental ? 'Fixed for period' : 'Prorated by present days'}</small>}</div> : <span>{rupees(0)}</span>}</td>
            <td>{hasVendorBilling ? <div className="ba-contract-billing"><strong>{rupees(vendorBilling)}</strong></div> : <span>{rupees(0)}</span>}</td>
            <td>{number(row.present)} {Number(row.present) === 1 ? 'day' : 'days'}</td>
            <td>{rupees(fuel)}</td>
            <td>{rupees(nonFuelExpenses)}</td>
            <td>{loanEmi ? <div className="ba-contract-billing"><strong>{rupees(loanEmi)}</strong><small>Due this period</small></div> : <span>{rupees(0)}</span>}</td>
            <td className={profit == null ? '' : `ba-profit-loss ${profit < 0 ? 'loss' : 'profit'}`}>{profit != null ? rupees(profit) : '—'}</td>
            <td>{margin != null ? `${number(margin)}%` : '—'}</td>
            <td><span className={`ba-performance-status ba-status-${tone}`}>{status}</span></td>
          </tr>;
        }) : <tr><td className="ba-performance-empty" colSpan="12">No vehicle activity was recorded for the selected period.</td></tr>}</tbody>
      </table>
    </div>
  </section>;
}
